import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  CheckCircle, ChevronRight, ChevronLeft, Scissors,
  AlertCircle, Loader2, X, Plus, Star, Calendar, Clock, Check,
} from 'lucide-react';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';
import { findTarif, formatPrix } from '../utils/tarifs';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const formatDuration = (min) => {
  if (!min) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}`;
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const timeToMinutes = (t) => {
  if (!t) return 0;
  const sep = t.includes('h') ? 'h' : ':';
  const [h, m] = t.split(sep).map(Number);
  return h * 60 + (m || 0);
};

const formatDate = (str) => {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
};

const formatDateLong = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' });
};

const DAYS_FR   = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
const MONTHS_FR = ['jan','fév','mar','avr','mai','juin','juil','août','sep','oct','nov','déc'];

const buildDays = (n = 28) => {
  const base = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const str = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return { str, day: DAYS_FR[d.getDay()], num: d.getDate(), month: MONTHS_FR[d.getMonth()], isToday: i === 0 };
  });
};

/* ─── DateStrip ───────────────────────────────────────────────────────────── */
function DateStrip({ days, selected, onSelect, isClosed }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!selected || !ref.current) return;
    const idx = days.findIndex(d => d.str === selected);
    if (idx < 0) return;
    ref.current.children[idx]?.scrollIntoView({ behavior:'smooth', inline:'center', block:'nearest' });
  }, [selected, days]);

  return (
    <div ref={ref} className="flex gap-2.5 overflow-x-auto" style={{ scrollbarWidth:'none', msOverflowStyle:'none', paddingBottom:4 }}>
      {days.map(d => {
        const closed = isClosed(d.str);
        const active = selected === d.str;
        return (
          <button
            key={d.str}
            onClick={() => !closed && onSelect(d.str)}
            disabled={closed}
            className={`flex-shrink-0 flex flex-col items-center w-[60px] py-3.5 rounded-2xl transition-all duration-200
              ${active
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                : closed
                ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer shadow-sm border border-slate-100'
              }`}
          >
            <span className={`text-[9px] font-black uppercase tracking-wider mb-1.5 ${active ? 'text-blue-200' : closed ? 'text-slate-300' : 'text-slate-400'}`}>
              {d.isToday ? 'Auj.' : d.day}
            </span>
            <span className="text-[22px] font-black leading-none">{d.num}</span>
            <span className={`text-[9px] font-bold mt-1.5 ${active ? 'text-blue-200' : closed ? 'text-slate-300' : 'text-slate-400'}`}>
              {d.month}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── ProgressHeader ──────────────────────────────────────────────────────── */
function ProgressHeader({ step, salonNom }) {
  const labels = ['Services', 'Date & heure', 'Coordonnées'];
  return (
    <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 sticky top-0 z-10">
      <div className="max-w-lg mx-auto px-5 pt-5 pb-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Scissors size={19} className="text-white" />
            </div>
            <div>
              <p className="font-black text-white text-base leading-none tracking-tight">{salonNom}</p>
              <p className="text-blue-200 text-xs font-semibold mt-0.5">Réservation en ligne</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white font-black text-lg leading-none">{step}<span className="text-blue-300 font-bold text-sm">/3</span></p>
            <p className="text-blue-200 text-[10px] font-semibold mt-0.5">{labels[step-1]}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-2">
          {[1,2,3].map(n => (
            <div key={n} className={`h-1.5 rounded-full transition-all duration-500 ${n <= step ? 'bg-white' : 'bg-white/25'} ${n === 1 ? 'flex-[2]' : n === 2 ? 'flex-[2]' : 'flex-[2]'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── SummaryRow ──────────────────────────────────────────────────────────── */
function SummaryRow({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-400 font-medium">{label}</span>
      <span className={`text-sm font-bold ${accent ? 'text-blue-600' : 'text-slate-800'}`}>{value}</span>
    </div>
  );
}

/* ─── StarDisplay ─────────────────────────────────────────────────────────── */
function StarDisplay({ rating, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={size} className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'} />
      ))}
    </div>
  );
}

/* ─── AvisSection ─────────────────────────────────────────────────────────── */
function AvisSection({ avis, salonNom }) {
  const avg = (avis.reduce((s, a) => s + a.rating, 0) / avis.length).toFixed(1);
  return (
    <div className="mt-14 pt-10 border-t-2 border-slate-100">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Avis clients</p>
          <h2 className="text-2xl font-black text-slate-800 leading-tight">{avis.length} témoignage{avis.length > 1 ? 's' : ''}</h2>
        </div>
        <div className="text-right pb-1">
          <p className="text-5xl font-black text-blue-600 leading-none">{avg}</p>
          <div className="flex justify-end mt-1">
            <StarDisplay rating={Math.round(Number(avg))} size={14} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {avis.map((a, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center font-black text-blue-600 text-base flex-shrink-0">
                  {(a.clientNom || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-sm">{a.clientNom || 'Client anonyme'}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {new Date(a.submittedAt).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' })}
                  </p>
                </div>
              </div>
              <StarDisplay rating={a.rating} size={12} />
            </div>
            {a.comment && (
              <p className="text-sm text-slate-600 leading-relaxed">{a.comment}</p>
            )}
            {a.adminReply && (
              <div className="mt-3 bg-blue-50 rounded-xl p-3.5 border-l-2 border-blue-400">
                <p className="text-xs font-black text-blue-600 mb-1 uppercase tracking-wide">{salonNom || 'Le salon'}</p>
                <p className="text-sm text-slate-700 leading-relaxed">{a.adminReply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main ────────────────────────────────────────────────────────────────── */
export default function BookingPage() {
  const { slug } = useParams();

  const getSession = () => {
    try { const r = sessionStorage.getItem(`booking_${slug}`); return r ? JSON.parse(r) : null; }
    catch { return null; }
  };
  const _s = getSession();

  const [pageState,  setPageState]  = useState('loading');
  const [salon,      setSalon]      = useState(null);
  const [categories, setCategories] = useState([]);
  const [avis,       setAvis]       = useState([]);
  const [step,       setStep]       = useState(_s?.step || 1);
  const topRef = useRef(null);
  const goStep = (n) => { setStep(n); setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); };

  const [selectedServices, setSelectedServices] = useState(_s?.selectedServices || []);
  const [pickingCategory,  setPickingCategory]  = useState(null);
  const [pickingDuration,  setPickingDuration]  = useState(null);

  const totalDuration = useMemo(
    () => selectedServices.reduce((sum, s) => sum + s.duration, 0),
    [selectedServices]
  );

  // Somme des prix des services (uniquement ceux qui ont un tarif pour leur durée)
  const totalPrix = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (findTarif(s.category, s.duration) || 0), 0),
    [selectedServices]
  );

  const [selectedDate,   setSelectedDate]   = useState(_s?.selectedDate || '');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [slots,          setSlots]          = useState([]);
  const [slotsLoading,   setSlotsLoading]   = useState(false);
  const [selectedSlot,   setSelectedSlot]   = useState(_s?.selectedSlot || null);

  const [form,       setForm]       = useState(_s?.form || { prenom:'', nom:'', telephone:'', email:'', notes:'' });
  const [formError,  setFormError]  = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (pageState === 'confirmed') sessionStorage.removeItem(`booking_${slug}`);
    else if (pageState === 'booking') {
      sessionStorage.setItem(`booking_${slug}`, JSON.stringify({ step, selectedServices, selectedDate, selectedSlot, form }));
    }
  }, [step, selectedServices, selectedDate, selectedSlot, form, pageState, slug]);

  const closedDays = useMemo(
    () => (salon?.horaires || []).filter(h => !h.ouvert).map(h => h.jour),
    [salon]
  );

  const isSalonClosed = useCallback(
    (dateStr) => {
      if (!dateStr || closedDays.length === 0) return false;
      return closedDays.includes(new Date(dateStr + 'T12:00:00').getDay());
    },
    [closedDays]
  );

  useEffect(() => {
    const fetchSalon = async () => {
      try {
        const [salonRes, avisRes] = await Promise.allSettled([
          axios.get(`${BASE_URL}/booking/${slug}`),
          axios.get(`${BASE_URL}/api/avis/salon/${slug}`),
        ]);
        if (salonRes.status === 'rejected') { setPageState('notfound'); return; }
        setSalon(salonRes.value.data.data.salon);
        setCategories(salonRes.value.data.data.categories || []);
        if (avisRes.status === 'fulfilled') setAvis(avisRes.value.data.data || []);
        setPageState('booking');
      } catch { setPageState('notfound'); }
    };
    fetchSalon();
  }, [slug]);

  const fetchSlots = useCallback(async (date, duration) => {
    if (!date || !duration) return;
    setSlotsLoading(true); setSlots([]); setSelectedSlot(null);
    try {
      const res = await axios.get(`${BASE_URL}/booking/${slug}/slots`, { params: { date, duration } });
      let data = res.data.data || [];
      if (date === todayStr()) {
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        data = data.filter(slot => timeToMinutes(slot.time) > nowMin + 30);
      }
      setSlots(data);
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  }, [slug]);

  useEffect(() => {
    if (step === 2 && selectedDate && totalDuration > 0 && !isSalonClosed(selectedDate)) {
      fetchSlots(selectedDate, totalDuration);
    }
    if (step === 2 && selectedDate && isSalonClosed(selectedDate)) {
      setSlots([]); setSelectedSlot(null);
    }
  }, [step, selectedDate, totalDuration, fetchSlots, isSalonClosed]);

  const handleDateChange = (date) => { setSelectedDate(date); setSelectedSlot(null); setSlots([]); };
  const handlePickCategory = (cat) => { setPickingCategory(c => c?._id === cat._id ? null : cat); setPickingDuration(null); };
  const handleAddService = () => {
    if (!pickingCategory || !pickingDuration || selectedServices.length >= 4) return;
    setSelectedServices(prev => [...prev, { category: pickingCategory, duration: pickingDuration }]);
    setPickingCategory(null); setPickingDuration(null);
    setSlots([]); setSelectedSlot(null);
  };
  const handleRemoveService = (index) => {
    setSelectedServices(prev => prev.filter((_, i) => i !== index));
    setSlots([]); setSelectedSlot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.prenom.trim() || !form.nom.trim() || !form.telephone.trim() || !form.email.trim()) {
      setFormError('Veuillez remplir tous les champs obligatoires.'); return;
    }
    if (!isValidEmail(form.email)) { setFormError('Adresse email invalide.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/booking/${slug}`, {
        clientPrenom: form.prenom.trim(), clientNom: form.nom.trim(),
        clientTelephone: form.telephone.trim(), clientEmail: form.email.trim(),
        notes: form.notes.trim() || undefined,
        date: selectedDate, heureDebut: selectedSlot.time, duration: totalDuration,
        services: selectedServices.map(s => ({ categoryId: s.category._id, duration: s.duration })),
        categoryId: selectedServices[0]?.category._id,
      });
      setConfirmation({ salonNom: salon?.nom, date: selectedDate, heure: selectedSlot.time, services: selectedServices, totalDuration, totalPrix, prenom: form.prenom });
      setPageState('confirmed');
    } catch (err) {
      if (err.response?.status === 409) setFormError('Ce créneau n\'est plus disponible, veuillez en choisir un autre.');
      else setFormError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally { setSubmitting(false); }
  };

  const resetBooking = () => {
    sessionStorage.removeItem(`booking_${slug}`);
    setStep(1); setSelectedServices([]); setPickingCategory(null); setPickingDuration(null);
    setSelectedDate(''); setSlots([]); setSelectedSlot(null); setShowCustomDate(false);
    setForm({ prenom:'', nom:'', telephone:'', email:'', notes:'' });
    setFormError(''); setConfirmation(null); setPageState('booking');
  };

  const ph = getPlaceholders(salon?.devise);

  /* ── Loading ── */
  if (pageState === 'loading') return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 to-blue-500 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-3xl bg-white/15 flex items-center justify-center border border-white/20">
          <Scissors size={28} className="text-white" />
        </div>
        <Loader2 size={28} className="text-white/60 animate-spin" />
      </div>
    </div>
  );

  /* ── Not Found ── */
  if (pageState === 'notfound') return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-5 px-6">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
        <Scissors size={26} className="text-slate-300" />
      </div>
      <div className="text-center">
        <h1 className="text-xl font-black text-slate-800 mb-2">Salon introuvable</h1>
        <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
          Ce lien de réservation ne correspond à aucun salon actif.
        </p>
      </div>
    </div>
  );

  /* ── Confirmation ── */
  if (pageState === 'confirmed' && confirmation) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 to-blue-500 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        {/* Big check */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center mx-auto mb-6">
            <Check size={48} className="text-white" strokeWidth={3} />
          </div>
          <p className="text-blue-200 text-xs font-black uppercase tracking-widest mb-2">C'est confirmé</p>
          <h1 className="text-3xl font-black text-white leading-tight">
            À très bientôt,<br/>{confirmation.prenom} !
          </h1>
        </div>

        {/* Receipt */}
        <div className="bg-white rounded-3xl overflow-hidden mb-5">
          <div className="bg-slate-50 px-5 py-4 border-b border-slate-100">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{confirmation.salonNom}</p>
          </div>
          <div className="px-5">
            {confirmation.services.map((s, i) => (
              <SummaryRow key={i}
                label={i === 0 ? (confirmation.services.length > 1 ? 'Services' : 'Service') : ''}
                value={`${s.category.nom} · ${formatDuration(s.duration)}`}
              />
            ))}
            {confirmation.services.length > 1 && <SummaryRow label="Durée" value={formatDuration(confirmation.totalDuration)} accent />}
            <SummaryRow label="Date"  value={formatDateLong(confirmation.date)} />
            <SummaryRow label="Heure" value={confirmation.heure} accent />
            {confirmation.totalPrix > 0 && <SummaryRow label="Total" value={formatPrix(confirmation.totalPrix, salon?.devise)} />}
          </div>
        </div>

        <p className="text-center text-blue-200/70 text-xs mb-5 font-medium">
          Un email de confirmation vous a été envoyé.
        </p>
        <button onClick={resetBooking}
          className="w-full py-4 bg-white text-blue-700 font-black rounded-2xl text-sm hover:bg-blue-50 transition-colors">
          Prendre un autre rendez-vous
        </button>
      </div>
    </div>
  );

  /* ── Main flow ── */
  return (
    <div className="min-h-screen bg-slate-100">
      <ProgressHeader step={step} salonNom={salon?.nom} />

      <div ref={topRef} className="max-w-lg mx-auto px-4 py-6 pb-36">

        {/* ── STEP 1 : Services ── */}
        {step === 1 && (
          <div className="flex flex-col gap-4">
            <div className="pt-1">
              <p className="text-xs font-black text-blue-500 uppercase tracking-widest mb-1">Étape 1</p>
              <h2 className="text-2xl font-black text-slate-800 leading-tight">Vos prestations</h2>
              <p className="text-sm text-slate-400 font-medium mt-1">Jusqu'à 4 services combinables.</p>
            </div>

            {/* Selected basket */}
            {selectedServices.length > 0 && (
              <div className="bg-blue-600 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-black text-blue-200 uppercase tracking-widest">Sélectionnés</p>
                  <span className="text-xs font-black text-white bg-white/20 px-2.5 py-0.5 rounded-full">
                    {selectedServices.length}/4 · {formatDuration(totalDuration)}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {selectedServices.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.category.couleur || '#fff' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm leading-none">{s.category.nom}</p>
                        <p className="text-blue-200 text-xs font-semibold mt-0.5">
                          {formatDuration(s.duration)}
                          {findTarif(s.category, s.duration) !== null && ` · ${formatPrix(findTarif(s.category, s.duration), salon?.devise)}`}
                        </p>
                      </div>
                      <button onClick={() => handleRemoveService(i)}
                        className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center text-white/70 hover:bg-white/30 flex-shrink-0">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
                {totalPrix > 0 && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/20">
                    <span className="text-sm font-black text-blue-100">Total</span>
                    <span className="text-lg font-black text-white">{formatPrix(totalPrix, salon?.devise)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Category cards */}
            {selectedServices.length < 4 && (
              <div>
                {selectedServices.length > 0 && (
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Ajouter un service</p>
                )}
                <div className="flex flex-col gap-2.5">
                  {categories
                    .filter(cat => !selectedServices.some(s => s.category._id === cat._id))
                    .map(cat => {
                      const isPicking = pickingCategory?._id === cat._id;
                      return (
                        <div key={cat._id}
                          className={`bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-200 ${isPicking ? 'shadow-md ring-2 ring-blue-500 ring-offset-1' : ''}`}>
                          {/* Category header */}
                          <button
                            onClick={() => handlePickCategory(cat)}
                            className="w-full flex items-stretch text-left"
                          >
                            <div className="w-1.5 flex-shrink-0 rounded-l-2xl" style={{ background: cat.couleur || '#2563eb' }} />
                            <div className="flex-1 flex items-center gap-3 px-4 py-4">
                              <div className="flex-1 min-w-0">
                                <p className="font-black text-slate-800 text-base leading-tight">{cat.nom}</p>
                                {cat.description && (
                                  <p className={`text-xs text-slate-400 font-medium mt-0.5 ${isPicking ? '' : 'line-clamp-2'}`}>{cat.description}</p>
                                )}
                              </div>
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all
                                ${isPicking ? 'bg-blue-600 rotate-90' : 'bg-slate-100'}`}>
                                <ChevronRight size={16} className={isPicking ? 'text-white' : 'text-slate-400'} />
                              </div>
                            </div>
                          </button>

                          {/* Duration picker — inline */}
                          {isPicking && (
                            <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 pt-3">
                                Choisissez la durée
                              </p>
                              <div className="flex flex-wrap gap-2 mb-4">
                                {(cat.durees || []).map(dur => (
                                  <button key={dur} onClick={() => setPickingDuration(dur)}
                                    className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all border-2
                                      ${pickingDuration === dur
                                        ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                                        : 'bg-slate-50 border-transparent text-slate-600 hover:border-blue-200 hover:bg-blue-50'
                                      }`}>
                                    {formatDuration(dur)}
                                    {findTarif(cat, dur) !== null && (
                                      <span className="block text-xs font-semibold opacity-80">{formatPrix(findTarif(cat, dur), salon?.devise)}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={handleAddService}
                                disabled={!pickingDuration}
                                className="w-full py-3 bg-blue-600 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-40">
                                <Plus size={16} />
                                {pickingDuration
                                  ? `Ajouter · ${cat.nom} ${formatDuration(pickingDuration)}${findTarif(cat, pickingDuration) !== null ? ` · ${formatPrix(findTarif(cat, pickingDuration), salon?.devise)}` : ''}`
                                  : 'Sélectionnez une durée'
                                }
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {categories.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
                <p className="text-slate-400 text-sm font-medium">Aucun service disponible pour ce salon.</p>
              </div>
            )}

            {/* CTA sticky sur mobile */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-slate-100 sm:static sm:bg-transparent sm:border-0 sm:p-0 sm:pt-2 flex justify-end z-30">
              <button onClick={() => goStep(2)} disabled={selectedServices.length === 0}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-4 bg-blue-600 text-white font-black rounded-2xl text-sm
                  shadow-lg shadow-blue-200 disabled:opacity-40 disabled:shadow-none hover:bg-blue-700 transition-all active:scale-95">
                Continuer <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2 : Date & Créneaux ── */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div className="pt-1">
              <p className="text-xs font-black text-blue-500 uppercase tracking-widest mb-1">Étape 2</p>
              <h2 className="text-2xl font-black text-slate-800 leading-tight">Date et créneau</h2>
            </div>

            {/* Services recap */}
            <div className="flex flex-wrap gap-2">
              {selectedServices.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
                  {s.category.nom} · {formatDuration(s.duration)}
                </span>
              ))}
              {selectedServices.length > 1 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">
                  <Clock size={11} />
                  {formatDuration(totalDuration)} au total
                </span>
              )}
              {totalPrix > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-black">
                  Total · {formatPrix(totalPrix, salon?.devise)}
                </span>
              )}
            </div>

            {/* Date strip */}
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Date</p>
              <DateStrip
                days={buildDays(28)}
                selected={selectedDate}
                onSelect={(d) => { handleDateChange(d); setShowCustomDate(false); }}
                isClosed={isSalonClosed}
              />

              {/* Custom date */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <button
                  onClick={() => setShowCustomDate(v => !v)}
                  className="flex items-center gap-2 text-xs font-black text-blue-500 hover:text-blue-700 transition-colors uppercase tracking-wide"
                >
                  <Calendar size={13} />
                  {showCustomDate ? 'Fermer' : 'Autre date'}
                </button>
                {showCustomDate && (
                  <input
                    type="date"
                    min={todayStr()}
                    value={selectedDate}
                    onChange={e => handleDateChange(e.target.value)}
                    className="mt-3 w-full px-4 py-3.5 rounded-xl border-2 border-blue-200 text-slate-800 text-sm font-bold focus:outline-none focus:border-blue-500 bg-slate-50"
                  />
                )}
              </div>
            </div>

            {/* Time slots */}
            {selectedDate && (
              <div className="bg-white rounded-2xl shadow-sm p-5">
                {isSalonClosed(selectedDate) ? (
                  <div className="py-8 text-center">
                    <p className="text-slate-400 text-sm font-medium">Le salon est fermé ce jour.</p>
                  </div>
                ) : slotsLoading ? (
                  <div className="py-10 flex flex-col items-center gap-3">
                    <Loader2 size={28} className="text-blue-500 animate-spin" />
                    <p className="text-xs text-slate-400 font-semibold">Recherche des créneaux…</p>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-slate-400 text-sm font-medium">Aucun créneau disponible pour cette date.</p>
                    <p className="text-slate-300 text-xs mt-1">Essayez une autre date.</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Créneaux</p>
                      <p className="text-xs font-bold text-slate-500 capitalize">{formatDateLong(selectedDate)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {slots.map(slot => {
                        const active = selectedSlot?.time === slot.time;
                        return (
                          <button key={slot.time} onClick={() => setSelectedSlot(slot)}
                            className={`h-14 rounded-xl text-base font-black border-2 transition-all duration-150 flex items-center justify-center gap-2
                              ${active
                                ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200 scale-[1.02]'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                              }`}>
                            {active && <Check size={15} strokeWidth={3} />}
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Selected summary bar */}
            {selectedDate && selectedSlot && (
              <div className="bg-blue-600 rounded-2xl px-4 py-3.5 flex items-center gap-3">
                <CheckCircle size={18} className="text-white flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-black text-sm capitalize">{formatDateLong(selectedDate)}</p>
                  <p className="text-blue-200 text-xs font-semibold mt-0.5">{selectedSlot.time} · {formatDuration(totalDuration)}</p>
                </div>
              </div>
            )}

            {/* CTA sticky sur mobile */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-slate-100 sm:static sm:bg-transparent sm:border-0 sm:p-0 sm:pt-2 flex items-center justify-between gap-3 z-30">
              <button onClick={() => goStep(1)}
                className="flex items-center gap-1.5 px-4 py-4 text-slate-500 font-bold text-sm hover:text-slate-800 transition-colors flex-shrink-0">
                <ChevronLeft size={17} /> Retour
              </button>
              <button onClick={() => goStep(3)} disabled={!selectedDate || !selectedSlot}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-4 bg-blue-600 text-white font-black rounded-2xl text-sm
                  shadow-lg shadow-blue-200 disabled:opacity-40 disabled:shadow-none hover:bg-blue-700 transition-all active:scale-95">
                Continuer <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3 : Coordonnées ── */}
        {step === 3 && (
          <div className="flex flex-col gap-4">
            <div className="pt-1">
              <p className="text-xs font-black text-blue-500 uppercase tracking-widest mb-1">Étape 3</p>
              <h2 className="text-2xl font-black text-slate-800 leading-tight">Vos coordonnées</h2>
              <p className="text-sm text-slate-400 font-medium mt-1">Les champs marqués * sont obligatoires.</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
              {/* Name row */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="p-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Prénom *</p>
                    <input required value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})}
                      placeholder={ph.prenom}
                      className="w-full text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent" />
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nom *</p>
                    <input required value={form.nom} onChange={e => setForm({...form, nom: e.target.value})}
                      placeholder={ph.nom}
                      className="w-full text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Téléphone *</p>
                <input required type="tel" value={form.telephone}
                  onChange={e => setForm({...form, telephone: filterPhone(e.target.value)})}
                  placeholder={ph.telephone} pattern="[\d\s+\-().]+"
                  className="w-full text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent" />
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Email *</p>
                <input required type="email" value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  placeholder={ph.email}
                  className="w-full text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent" />
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Notes (facultatif)</p>
                <textarea rows={3} value={form.notes}
                  onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder={ph.notes}
                  className="w-full text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent resize-none" />
              </div>

              {/* Recap card */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-3">
                  <p className="text-xs font-black text-blue-100 uppercase tracking-widest">Récapitulatif</p>
                </div>
                <div className="px-4">
                  {selectedServices.map((s, i) => (
                    <SummaryRow key={i}
                      label={i === 0 ? (selectedServices.length > 1 ? 'Services' : 'Service') : ''}
                      value={`${s.category.nom} · ${formatDuration(s.duration)}`}
                    />
                  ))}
                  {selectedServices.length > 1 && <SummaryRow label="Durée" value={formatDuration(totalDuration)} accent />}
                  <SummaryRow label="Date"  value={formatDateLong(selectedDate)} />
                  <SummaryRow label="Heure" value={selectedSlot?.time || '-'} accent />
                </div>
              </div>

              {formError && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border-l-4 border-red-400 rounded-xl">
                  <AlertCircle size={17} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-700 font-semibold leading-snug">{formError}</span>
                </div>
              )}

              {/* CTA sticky sur mobile */}
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-slate-100 sm:static sm:bg-transparent sm:border-0 sm:p-0 sm:pt-2 flex items-center justify-between gap-3 z-30">
                <button type="button" onClick={() => goStep(2)}
                  className="flex items-center gap-1.5 px-4 py-4 text-slate-500 font-bold text-sm hover:text-slate-800 transition-colors flex-shrink-0">
                  <ChevronLeft size={17} /> Retour
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-4 bg-blue-600 text-white font-black rounded-2xl text-sm
                    shadow-lg shadow-blue-200 disabled:opacity-60 disabled:shadow-none hover:bg-blue-700 transition-all active:scale-95">
                  {submitting ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle size={17} />}
                  Confirmer le rendez-vous
                </button>
              </div>
            </form>
          </div>
        )}

        {avis.length > 0 && <AvisSection avis={avis} salonNom={salon?.nom} />}
      </div>
    </div>
  );
}
